import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-control)] text-sm font-medium tracking-[-0.01em] transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
    {
        variants: {
            variant: {
                primary:
                    "bg-brand-600 text-white hover:bg-brand-700 active:scale-[0.98] active:bg-brand-800 shadow-none",
                secondary:
                    "bg-[var(--surface-inset)] text-neutral-700 hover:bg-neutral-100 active:scale-[0.98] active:bg-neutral-200 dark:bg-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-600 dark:active:bg-neutral-500",
                outline:
                    "border bg-[var(--surface-primary)] text-neutral-700 hover:bg-[var(--surface-inset)] hover:border-neutral-300 active:scale-[0.98] dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700 dark:hover:border-neutral-500",
                ghost: "text-neutral-600 hover:bg-[var(--surface-inset)] hover:text-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-700 dark:hover:text-neutral-200",
                danger: "bg-error text-white hover:bg-error-dark active:scale-[0.98] shadow-none",
            },
            size: {
                sm: "h-9 px-3.5 text-xs",
                md: "h-11 px-5 text-sm",
                lg: "h-12 px-6 text-sm",
                icon: "h-11 w-11",
            },
        },
        defaultVariants: {
            variant: "primary",
            size: "md",
        },
    },
);

export interface ButtonProps
    extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
    loading?: boolean;
    debugState?: "live" | "demo" | "placeholder" | "mock";
    debugActionId?: string;
}

const springHover = {
    scale: 1.02,
    transition: { type: "spring" as const, stiffness: 400, damping: 20 },
};
const springTap = {
    scale: 0.97,
    transition: { type: "spring" as const, stiffness: 500, damping: 25 },
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size,
            loading,
            disabled,
            debugState,
            debugActionId,
            children,
            // Strip React-specific drag/animation event handlers that conflict with Framer Motion types
            onDrag: _d,
            onDragStart: _ds,
            onDragEnd: _de,
            onAnimationStart: _as,
            ...rest
        },
        ref,
    ) => {
        return (
            <motion.button
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                disabled={disabled || loading}
                data-debug-state={debugState}
                data-debug-action-id={debugActionId}
                whileHover={springHover}
                whileTap={springTap}
                {...(rest as Record<string, unknown>)}
            >
                {loading && (
                    <div className="h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
                )}
                {children}
            </motion.button>
        );
    },
);
Button.displayName = "Button";

export { buttonVariants };
